/**
 * The onboarding Modal, rendered above the routes (`spec-onboarding-persistent-scene-launch-into-live`,
 * R2), beside `PlanHandoverLayer` in `ProtectedGate`. It renders what `onboardingModalStore` holds,
 * so the summary the landing opens stays one open Modal (one Scrim, focus kept inside it) while the
 * app moves from `/onboarding` to `/twin` behind it.
 *
 * - **In the main chunk, carrying no onboarding code (R2.4).** It imports the store (types and plain
 *   functions) and React only. The Modal's content (`OnboardingModal` and what it imports) is a lazy
 *   import, first loaded when the store first holds something to show; until then it renders
 *   nothing.
 * - **Never over another session (R2.3).** A carried summary is released, closed with no fade, when
 *   the signed-in user changes (`userKey`, the Auth0 `sub`) or the protected layout unmounts.
 */
import React, {
  Suspense,
  lazy,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ComponentType,
} from 'react';

import {
  backToSummary,
  confirmRequest,
  getOnboardingModal,
  releaseCarried,
  settleRequest,
  shownOf,
  showComparison,
  subscribeOnboardingModal,
} from './onboardingModalStore';

type OnboardingModalProps = React.ComponentProps<typeof import('./OnboardingModal').default>;

/**
 * The Modal's chunk could not be fetched (a deploy replaced it under a long-open tab, or the
 * network dropped). Rather than throw past `ProtectedGate`, which has no boundary and would blank
 * the whole app, the request is settled `'cancel'`: the safe answer to every request (no launch
 * after the changes check, stay on a leave prompt, a summary dismissed). Logged, so it is visible.
 * Launch's activating content is not a request: it is left to the run that holds it, and nothing is
 * settled, since settling with no request would publish a new snapshot and run this again.
 */
const ModalUnavailable = ({ state }: OnboardingModalProps) => {
  const isRequest = state.kind !== 'activating';
  useEffect(() => {
    if (isRequest) settleRequest('cancel');
  }, [state, isRequest]);
  return null;
};

let modalChunk: Promise<{ default: ComponentType<OnboardingModalProps> }> | null = null;

/**
 * One fetch of the Modal's chunk, shared by the preload and the first render. A failed preload is
 * forgotten, so the first render tries again; a failed first render falls back to
 * `ModalUnavailable` for the session.
 */
export const preloadOnboardingModal = () => {
  modalChunk ??= import('./OnboardingModal').catch((error: unknown) => {
    modalChunk = null;
    // eslint-disable-next-line no-console
    console.error('[onboarding-modal] the Modal could not be loaded', error);
    return { default: ModalUnavailable };
  });
  return modalChunk;
};

const OnboardingModal = lazy(preloadOnboardingModal);

const onConfirm = () => confirmRequest();
const onCancel = () => settleRequest('cancel');
// Escape, the scrim and the close button acknowledge the summary, never run its retry.
const onDismiss = () => settleRequest('confirm');

export const OnboardingModalLayer = ({ userKey }: { userKey: string | null }) => {
  const snapshot = useSyncExternalStore(
    subscribeOnboardingModal,
    getOnboardingModal,
    getOnboardingModal,
  );

  // R2.3: the signed-in user changed under a carried summary.
  const userRef = useRef(userKey);
  useEffect(() => {
    if (userRef.current !== userKey) releaseCarried();
    userRef.current = userKey;
  }, [userKey]);

  // R2.3: the protected layout unmounted (sign-out, a route outside it).
  useEffect(() => () => releaseCarried(), []);

  const shown = shownOf(snapshot);
  if (shown === null) return null;

  return (
    <Suspense fallback={null}>
      <OnboardingModal
        state={shown}
        comparison={snapshot.comparison}
        onConfirm={onConfirm}
        onDismiss={onDismiss}
        onCancel={onCancel}
        onShowComparison={showComparison}
        onBackToSummary={backToSummary}
      />
    </Suspense>
  );
};
